
import { supabase } from "@/integrations/supabase/client";
import { Device, InstalledApp, isValidLastKnownData, convertToInstalledApp } from "@/types/device";
import { generateMockDevices } from "@/data/mockDevices";
import { getTenantInfo } from "@/hooks/useDevices";
import { DEVICES_QUERY_KEY } from "@/hooks/useDevices";
import { getDeviceStatus } from "@/utils/deviceStatus";

export interface FetchDevicesParams {
  page?: number;
  pageSize?: number;
}

export interface DeviceTag {
  id: string;
  name: string;
  color: string;
  icon?: string;
  type: string;
}

export interface FetchDevicesResult {
  devices: Device[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  deviceTags: Map<string, DeviceTag[]>;
  statusCounts: {
    active: number;
    inactive: number;
    pending: number;
    total: number;
  };
}

// Fetch device status counts (lightweight query for all devices)
export const fetchDeviceStatusCounts = async () => {
  const { data, error } = await supabase
    .from('devices')
    .select('id, status, last_seen, created_at');
  
  if (error) throw error;
  
  // Calculate counts by status
  const counts = (data || []).reduce((acc, device) => {
    const status = getDeviceStatus(device.last_seen || device.created_at, device.status);
    acc[status] = (acc[status] || 0) + 1;
    acc.total++;
    return acc;
  }, { active: 0, inactive: 0, pending: 0, unenrolled: 0, total: 0 });
  
  return counts;
};

export const fetchDevices = async (params: FetchDevicesParams = {}): Promise<FetchDevicesResult> => {
  try {
    const { page = 1, pageSize = 25 } = params;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    console.log(`Fetching devices page ${page} (${from}-${to})...`);
    
    // Get total count first
    const { count, error: countError } = await supabase
      .from('devices')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      console.error('Error counting devices:', countError);
      throw countError;
    }

    const totalCount = count || 0;
    console.log(`Total devices in database: ${totalCount}`);

    // Fetch paginated devices without heavy joins
    const { data: devicesData, error: devicesError } = await supabase
      .from('devices')
      .select('*')
      .order('created_at', { ascending: false })
      .range(from, to);

    if (devicesError) {
      console.error('Error fetching devices:', devicesError);
      throw devicesError;
    }

    console.log(`Fetched ${devicesData?.length || 0} devices for page ${page}`);

    if (!devicesData || devicesData.length === 0) {
      console.log('No devices found for this page');
      const statusCounts = await fetchDeviceStatusCounts();
      return {
        devices: [],
        total: totalCount,
        page,
        pageSize,
        totalPages: Math.ceil(totalCount / pageSize),
        deviceTags: new Map(),
        statusCounts
      };
    }

    // Get device IDs for fetching related data
    const deviceIds = devicesData.map(d => d.id);

    // Fetch installed apps separately for this page of devices
    const { data: installedAppsData } = await supabase
      .from('installed_apps')
      .select('*')
      .in('device_id', deviceIds);

    // Fetch all tags for all devices in a single query (Phase 1 optimization)
    const { data: deviceTagsData } = await supabase
      .from('device_tags')
      .select(`
        device_id,
        tags!inner (
          id,
          name,
          color,
          icon,
          type
        )
      `)
      .in('device_id', deviceIds);

    // Create a map of device_id -> apps
    const appsMap = new Map<string, any[]>();
    installedAppsData?.forEach(app => {
      if (!appsMap.has(app.device_id)) {
        appsMap.set(app.device_id, []);
      }
      appsMap.get(app.device_id)!.push(app);
    });

    // Create a map of device_id -> tags (Phase 1)
    const tagsMap = new Map<string, DeviceTag[]>();
    deviceTagsData?.forEach((item: any) => {
      if (!tagsMap.has(item.device_id)) {
        tagsMap.set(item.device_id, []);
      }
      if (item.tags) {
        tagsMap.get(item.device_id)!.push(item.tags);
      }
    });

    // Process devices with their apps
    const devicesWithApps = devicesData.map(device => {
      const deviceApps = appsMap.get(device.id) || [];
      return processDeviceFromDb({ ...device, installed_apps: deviceApps });
    });

    // Fetch status counts for all devices
    const statusCounts = await fetchDeviceStatusCounts();

    console.log('Returning processed devices data:', devicesWithApps.length);
    return {
      devices: devicesWithApps,
      total: totalCount,
      page,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
      deviceTags: tagsMap,
      statusCounts
    };
  } catch (error) {
    console.error('Error in fetchDevices:', error);
    throw error;
  }
};

// Helper function to process a device from the database
const processDeviceFromDb = (device: any): Device => {
  try {
    console.log(`Processing device ${device.id}...`);
    
    const rawLastKnownData = device.last_known_data as unknown;
    const lastKnownData = isValidLastKnownData(rawLastKnownData) ? rawLastKnownData : null;
    
    // Get installed apps from the joined installed_apps table
    const installedApps: InstalledApp[] = Array.isArray(device.installed_apps) 
      ? device.installed_apps.map((app: any) => ({
          name: app.name,
          version: app.version || null,
          installed_date: app.installed_date,
          bundle_id: app.bundle_id || undefined,
          icon: app.icon || undefined
        }))
      : [];

    // Get tenant info for this device
    const tenantInfo = getTenantInfo(device.id);

    const deviceData: Device = {
      id: device.id,
      name: device.name,
      os_type: device.os_type || '',
      os_version: device.os_version || lastKnownData?.os_version || '',
      status: device.status || 'unknown',
      last_seen: device.last_seen || device.created_at,
      created_at: device.created_at,
      hostname: device.hostname || '',
      battery_level: device.battery_level ?? lastKnownData?.battery_level ?? null,
      battery_status: device.battery_status ?? lastKnownData?.battery_status ?? null,
      ip_address: device.ip_address ?? lastKnownData?.ip_address ?? null,
      serial_number: device.serial_number || null,
      installed_apps: installedApps,
      department: null,
      location: null,
      customer: tenantInfo.customer,
      tenantId: tenantInfo.tenantId,
      script_deployments: device.script_deployments
    };

    // Remove duplicate apps - prefer entries with bundle_id
    const uniqueApps = Array.from(new Map(
      deviceData.installed_apps
        .sort((a, b) => {
          // Sort so apps WITH bundle_id come first
          if (a.bundle_id && !b.bundle_id) return -1;
          if (!a.bundle_id && b.bundle_id) return 1;
          return 0;
        })
        .map(app => [
          app.bundle_id || `${app.name}-${app.version}`,
          app
        ])
    ).values());

    return {
      ...deviceData,
      installed_apps: uniqueApps
    };
  } catch (error) {
    console.error('Error processing device:', device.id, error);
    const tenantInfo = getTenantInfo(device.id);
    return {
      id: device.id,
      name: device.name || 'Unknown Device',
      os_type: device.os_type || 'Unknown',
      os_version: device.os_version || '',
      status: device.status || 'unknown',
      last_seen: device.last_seen || device.created_at,
      created_at: device.created_at,
      hostname: device.hostname || '',
      battery_level: null,
      battery_status: null,
      serial_number: device.serial_number || null,
      ip_address: null,
      installed_apps: [],
      department: null,
      location: null,
      customer: tenantInfo.customer,
      tenantId: tenantInfo.tenantId
    };
  }
};

// Function to seed the database with mock devices - with better error handling
export const seedMockDevices = async (): Promise<void> => {
  try {
    console.log('Seeding database with mock devices...');
    const mockDevices = generateMockDevices();
    console.log(`Generated ${mockDevices.length} mock devices for seeding`);
    
    // Prepare devices for database insertion
    const devicesForDb = mockDevices.map(device => {
      // Generate a valid UUID for each device instead of using the descriptive ID
      const dbId = crypto.randomUUID();
      
      // Convert installedApps to a format acceptable for JSON storage
      const installedAppsJson = device.installed_apps.map(app => ({
        name: app.name,
        version: app.version,
        installed_date: app.installed_date
      }));
      
      // Store the original device ID and tenant info in the last_known_data field
      const tenantInfo = getTenantInfo(device.id);
      
      // Create a DB-friendly object with only the fields we want to store
      return {
        id: dbId, 
        name: device.name,
        os_type: device.os_type,
        os_version: device.os_version,
        status: device.status,
        last_seen: device.last_seen,
        created_at: device.created_at,
        hostname: device.hostname,
        battery_level: device.battery_level,
        battery_status: device.battery_status,
        serial_number: device.serial_number,
        ip_address: device.ip_address,
        installed_apps: installedAppsJson,
        customer_id: null, // Map to customer_id in DB schema
        // Custom fields stored outside the database schema
        last_known_data: {
          original_id: device.id,
          tenant_customer: tenantInfo.customer,
          tenant_id: tenantInfo.tenantId,
          battery_level: device.battery_level,
          battery_status: device.battery_status,
          ip_address: device.ip_address,
          os_version: device.os_version,
          installed_apps: installedAppsJson
        }
      };
    });
    
    // Insert in batches to avoid request size limits, with better error logging
    const batchSize = 5;
    for (let i = 0; i < devicesForDb.length; i += batchSize) {
      const batch = devicesForDb.slice(i, i + batchSize);
      
      try {
        const { error } = await supabase
          .from('devices')
          .upsert(batch, { 
            onConflict: 'id',
            ignoreDuplicates: false 
          });
          
        if (error) {
          console.error(`Error inserting batch ${i / batchSize + 1}:`, error);
          // Don't throw, just log and continue with other batches
        } else {
          console.log(`Successfully inserted batch ${i / batchSize + 1} of mock devices`);
        }
      } catch (batchError) {
        console.error(`Exception in batch ${i / batchSize + 1}:`, batchError);
        // Continue with other batches
      }
    }
    
    console.log('Finished seeding database with mock devices');
  } catch (error) {
    console.error('Error seeding mock devices:', error);
    throw error; // Rethrow to allow caller to handle
  }
};

// Helper function to update device cache that doesn't rely on React hooks
export const broadcastDeviceUpdate = () => {
  if (typeof window !== 'undefined') {
    // Trigger an update to the localStorage to notify other tabs
    const timestamp = new Date().getTime();
    localStorage.setItem('device-cache-updated', timestamp.toString());
    
    // Also dispatch an event for the current tab
    const event = new Event('device-cache-updated');
    window.dispatchEvent(event);
    
    console.log('Device update broadcast to all tabs');
  }
};
